import axios, { AxiosResponse } from "axios";

import TemplateController from "./template-controller";
import EventController from "./event-controller";
import OutputController from "./output-controller";
import {
  IImageMeta,
  ILabelOptionValue,
  IOption,
  IReactConfiguration,
} from "../../typings";

interface IColor {
  hex: string;
  percentage: number;
  pantone?: string;
}

interface IDlsColorExtractorResponse {
  success: boolean;
  data: { colors: IColor[] };
}

interface IColorValuesForItem {
  hex: string;
  value: string;
  rgb: [number, number, number];
}

interface IColorValuesDiff extends IColorValuesForItem {
  diff: number;
}

export default class ColorController {
  private templateController: TemplateController;
  private outputController: OutputController;
  private eventController: EventController;
  private conf: IReactConfiguration;

  private colorValuesForItem: IColorValuesForItem[];
  private lastSrc: string;

  constructor(
    templateController: TemplateController,
    eventController: EventController,
    outputController: OutputController,
    conf: IReactConfiguration,
  ) {
    this.templateController = templateController;
    this.eventController = eventController;
    this.outputController = outputController;
    this.conf = conf;
  }

  get lastColors(): IColor[] {
    return this.outputController.getValue(["image", "color-auto-detected"]);
  }

  set lastColors(colors: IColor[]) {
    this.outputController.setValue(["image", "color-auto-detected"], [colors]);
  }

  public resetColors(): Promise<unknown> {
    if (this.lastColors && this.lastColors.length > 0) {
      this.setColorText(this.lastColors);
      return Promise.resolve();
    }

    return new Promise((resolve: (value: any) => void, reject: () => void) => {
      const xhr: XMLHttpRequest = new XMLHttpRequest();
      xhr.onload = () => {
        const reader: FileReader = new FileReader();
        reader.onloadend = () => {
          this.updateColors((reader as any).result)
            .then(resolve)
            .catch(reject);
        };
        reader.readAsDataURL(xhr.response);
      };
      xhr.open(
        "GET",
        this.outputController.getValue(["image", "original-png"]),
      );
      xhr.responseType = "blob";
      xhr.send();
    });
  }

  public updateColors(imageSrc: string): Promise<void> {
    if (this.templateController.getOptionByCode("color-percentage") === null) {
      return Promise.resolve();
    }

    const option: IOption =
      this.templateController.getOptionByCode("color-text");
    if (!option) {
      return Promise.resolve();
    }

    const meta: IImageMeta = this.outputController.getValue([
      "image",
      "original-meta",
    ]);

    if (!meta) {
      return Promise.resolve();
    }

    if (imageSrc === "data:,") {
      return Promise.resolve();
    }

    if (this.lastSrc === imageSrc) {
      this.setColorText(this.lastColors);
    }
    return axios
      .post<IDlsColorExtractorResponse>(
        "https://grpmsjfglsmtd7ya47rjo3ytm40drfvr.lambda-url.eu-central-1.on.aws/",
        { imageUrl: imageSrc },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
      .then(
        (response: AxiosResponse<IDlsColorExtractorResponse>) => response.data,
      )
      .then((response: IDlsColorExtractorResponse) => {
        this.lastSrc = imageSrc;

        const colorsInputSorted: IColor[] = ColorController.sortColors(
          response.data.colors,
        );

        const colors: IColorValuesForItem[] = this.getColorValuesForItem();
        this.lastColors = colorsInputSorted.map((colorInput: IColor) => {
          const match: IColorValuesDiff =
            ColorController.calculateDifOfColorsAndGetClosestMatch(
              colorInput,
              colors,
            );
          return {
            ...colorInput,
            pantone: match.value,
          };
        });
        this.setColorText(response.data.colors);
      });
  }

  private static sortColors(colorsInput: IColor[]): IColor[] {
    return colorsInput.sort(
      (a: IColor, b: IColor) => b.percentage - a.percentage,
    );
  }

  private setColorText(colorsInput: IColor[]): void {
    const colorsInputSorted: IColor[] = ColorController.sortColors(colorsInput);

    const colors: IColorValuesForItem[] = this.getColorValuesForItem();
    const newColors: string[] = colorsInputSorted.map((colorInput: IColor) => {
      const match: IColorValuesDiff =
        ColorController.calculateDifOfColorsAndGetClosestMatch(
          colorInput,
          colors,
        );
      return match.value;
    });

    const newColorsPercentage: number[] = colorsInputSorted.map(
      (colorInput: IColor) => colorInput.percentage,
    );

    this.outputController
      .setValue(["image", "color-text"], [newColors])
      .then(() => {})
      .catch(() => {});

    this.outputController
      .setValue(["image", "color-percentage"], [newColorsPercentage])
      .then(() => {})
      .catch(() => {});

    this.outputController
      .setValue(["image", "factory-color"], [newColors.map(() => "")])
      .then(() => {})
      .catch(() => {});
  }

  private static calculateDifOfColorsAndGetClosestMatch(
    colorInput: IColor,
    colors: IColorValuesForItem[],
  ): IColorValuesDiff {
    const inputRgb: [number, number, number] = this.calculateColorDecimalValue(
      colorInput.hex,
    );

    return colors
      .map((color: IColorValuesForItem) => ({
        ...color,
        diff:
          Math.abs(inputRgb[0] - color.rgb[0]) +
          Math.abs(inputRgb[1] - color.rgb[1]) +
          Math.abs(inputRgb[2] - color.rgb[2]),
      }))
      .sort((a: IColorValuesDiff, b: IColorValuesDiff) => a.diff - b.diff)[0];
  }

  private static calculateColorDecimalValue(
    hexInput: string,
  ): [number, number, number] {
    const hex: string = hexInput.replace("#", "").slice();
    const r: number = parseInt("0x" + hex.slice(0, 2));
    const g: number = parseInt("0x" + hex.slice(2, 4));
    const b: number = parseInt("0x" + hex.slice(4, 6));
    return [r, g, b];
  }

  private getColorValuesForItem(): IColorValuesForItem[] {
    if (typeof this.colorValuesForItem !== "undefined") {
      return this.colorValuesForItem;
    }

    const colorOptionValues: Array<ILabelOptionValue> =
      this.templateController.getOptionByCode("color-text").values;

    return colorOptionValues.map((cov: ILabelOptionValue) => ({
      hex: cov.attributes.hex.value,
      value: cov.value,
      rgb: ColorController.calculateColorDecimalValue(cov.attributes.hex.value),
    }));
  }
}
